export function tierUnitPrice(tiers, qty) {
  if (!tiers || tiers.length === 0) return 0;
  const sorted = [...tiers].sort((a, b) => a.min_qty - b.min_qty);
  let price = sorted[0].price;
  for (const t of sorted) {
    if (qty >= t.min_qty) price = t.price;
  }
  return price;
}

export function gbp(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);
}

export function formatWeight(g) {
  if (g >= 1000) return `${(g / 1000).toFixed(2)} kg`;
  return `${g} g`;
}

// Client-side mirror of the backend shipping computation (server remains authoritative at checkout).
export function estimateShipping(zones, country, weightG, subtotal) {
  if (!zones || zones.length === 0) return null;
  let zone = zones.find((z) => (z.countries || []).includes(country));
  if (!zone) zone = [...zones].sort((a, b) => b.tier - a.tier)[0];
  if (!zone) return null;
  const brackets = [...(zone.brackets || [])].sort((a, b) => a.min_weight - b.min_weight);
  let matched = brackets.find((b) => weightG >= b.min_weight && weightG <= b.max_weight);
  if (!matched && brackets.length) matched = brackets[brackets.length - 1];
  let price = matched ? matched.price : 0;
  let free = false;
  const threshold = zone.free_shipping_threshold;
  if (threshold != null && subtotal >= threshold) { price = 0; free = true; }
  return { price, free, zoneName: zone.name, threshold };
}

export function zoneCountries(zones) {
  const set = new Set();
  (zones || []).forEach((z) => (z.countries || []).forEach((c) => set.add(c)));
  return Array.from(set).sort((a, b) => (a === "United Kingdom" ? -1 : b === "United Kingdom" ? 1 : a.localeCompare(b)));
}

// Strength add-ons + mixed-size discount are admin-editable (GET /api/pricing-rules); these are the fallbacks.
export const DEFAULT_PRICING_RULES = {
  boosts: {
    standard: { label: "Standard", note: "", mult: 1.0 },
    x2: { label: "×2 Double Strength", note: "", mult: 1.4 },
    x3: { label: "×3 Triple Strength", note: "", mult: 1.75 },
  },
  mix_discount_pct: 5,
  trade_10l_discount_pct: 5,
  trade_20l_discount_pct: 10,
  bottle_offer: { enabled: false, size_ml: 60, price_1: 9.5, price_2: 15, max_qty: 2, headline: "", sub: "" },
  guest_bundle_pct: 0,
};
let RULES = DEFAULT_PRICING_RULES;
let TRADE = false;
export const setPricingRules = (rules) => { if (rules && rules.boosts) RULES = { ...DEFAULT_PRICING_RULES, ...rules }; };
export const setTradeMode = (on) => { TRADE = !!on; };
export const getPricingRules = () => RULES;
export const boostMult = (code) => Number(RULES.boosts[code]?.mult) || 1;
export const boostLabel = (code) => RULES.boosts[code]?.label || "Standard";
export const boostNote = (code) => RULES.boosts[code]?.note || "";
export const mixDiscountRate = () => (Number(RULES.mix_discount_pct) || 0) / 100;
export const mixDiscountPct = () => Number(RULES.mix_discount_pct) || 0;
// Trade (dropship) mix-bundle volume saving: 5% at 10L, 10% at 20L. Zero for non-trade sessions.
export const tradeVolumeRate = (totalMl) => {
  if (!TRADE) return 0;
  if (totalMl >= 20000) return (Number(RULES.trade_20l_discount_pct) || 0) / 100;
  if (totalMl >= 10000) return (Number(RULES.trade_10l_discount_pct) || 0) / 100;
  return 0;
};
export const isTradeMode = () => TRADE;
export const guestBundlePct = () => Number(RULES.guest_bundle_pct) || 0;
// Per-bottle price for a Premium (guest) product at `qty` from a [{min_qty,price}] ladder.
export const guestTierPrice = (tiers, qty, fallback = 0) => {
  let best = null;
  for (const t of (tiers || []).slice().sort((a, b) => Number(a.min_qty) - Number(b.min_qty))) {
    if (qty >= Number(t.min_qty)) best = Number(t.price);
  }
  return best != null ? best : Number(fallback) || 0;
};
export const bottleOffer = () => RULES.bottle_offer || DEFAULT_PRICING_RULES.bottle_offer;
export const offerQtyInCart = (items) => items.reduce((s, i) => (i.type === "offer" ? s + (i.quantity || 0) : s), 0);
// Promo unit price given the total number of offer bottles in the order (1 → price_1, 2 → price_2 / 2).
export const offerUnitPrice = (totalQty) => {
  const o = bottleOffer();
  return r2(totalQty >= 2 && Number(o.price_2) > 0 ? Number(o.price_2) / 2 : Number(o.price_1) || 0);
};
export const offerHeadline = (sizeLabel) => {
  const o = bottleOffer();
  if (o.headline) return o.headline;
  const two = Number(o.price_2) > 0 ? ` · or 2 for ${gbp(o.price_2)}` : "";
  return `Add a ${sizeLabel || `${o.size_ml}ml`} bottle for only ${gbp(o.price_1)}${two}`;
};
// Price of the next offer bottle the customer could add (0 when the offer is off or already maxed).
export const nextOfferStep = (items) => {
  const o = bottleOffer();
  if (!o.enabled || !(Number(o.price_1) > 0)) return 0;
  const n = offerQtyInCart(items);
  if (n === 0) return Number(o.price_1);
  if (n === 1 && Number(o.price_2) > 0) return r2(Number(o.price_2) - Number(o.price_1));
  return 0;
};
// Re-price offer bottles from the live rule so 2 bottles always land on the 2-for price.
export function normalizeOffers(items) {
  const total = offerQtyInCart(items);
  if (!total) return items;
  const unit = offerUnitPrice(total);
  return items.map((i) => (i.type === "offer" ? { ...i, unit_price: unit, promo_price_1: bottleOffer().price_1 } : i));
}
// Ordered list for pickers: [{ code, label, short, note, mult }]
export const boostEnabled = (code) => code === "standard" || RULES.boosts[code]?.enabled !== false;
export const boostOptions = () => ["standard", "x2", "x3"].filter(boostEnabled).map((code) => ({
  code, label: boostLabel(code), note: boostNote(code), mult: boostMult(code),
  short: code === "standard" ? "Std" : code === "x2" ? "×2" : "×3",
}));
const r2 = (n) => Math.round(n * 100) / 100;

// Build a mixbox cart item. Price fields (base_unit_price/box_price/mixpack_discount + per-selection
// prices) are recomputed by normalizeMixboxes on add, so callers only supply the composition.
export function makeMixboxItem(size, selections, { tiers, isWholesale = false, accentColor } = {}) {
  const bottle_count = selections.reduce((a, s) => a + (s.quantity || 0), 0);
  return {
    key: `mixbox-${size.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type: "mixbox",
    size_id: size.id, size_label: size.label, bottle_count,
    selections, quantity: 1,
    tiers: tiers || size.tiers, size_weight_g: size.weight_g, size_ml: size.ml, retail_tiers: size.retail_tiers || null,
    weight_g: (size.weight_g || 0) * bottle_count,
    is_wholesale: isWholesale, accent_color: accentColor,
  };
}

// Build an economy cart item. Prices are supplied already-computed by the builder
// (flat per-size economy price × strength mult), and the server re-derives them at checkout.
export function makeEconomyItem(size, selections) {
  const bottle_count = selections.reduce((a, s) => a + (s.quantity || 0), 0);
  const box_price = r2(selections.reduce((a, s) => a + (s.line_total || 0), 0));
  return {
    key: `economy-${size.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type: "economy",
    size_id: size.id, size_label: size.label, size_ml: size.ml, size_weight_g: size.weight_g,
    bottle_count, selections, quantity: 1,
    unit_price: box_price, box_price,
    weight_g: (size.weight_g || 0) * bottle_count,
    accent_color: selections[selections.length - 1]?.accent_color || "#9AA0A6",
  };
}

// Merge mixbox cart items by bottle size, re-tier at the combined bottle count, and apply the
// established 5% mixed-size saving when an order spans 2+ bottle sizes. The server mirrors this
// from the actual size groups and remains authoritative at checkout.
export function normalizeMixboxes(items) {
  const passthrough = items.filter((i) => !(i.type === "mixbox" && Array.isArray(i.tiers) && i.tiers.length));
  const mixes = items.filter((i) => i.type === "mixbox" && Array.isArray(i.tiers) && i.tiers.length);
  if (mixes.length === 0) return items;

  const bySize = new Map();
  for (const it of mixes) {
    const q = it.quantity || 1;
    const cur = bySize.get(it.size_id) || {
      key: it.key, size_id: it.size_id, size_label: it.size_label, tiers: it.tiers,
      size_weight_g: it.size_weight_g ?? (it.bottle_count ? Math.round((it.weight_g || 0) / it.bottle_count) : 0),
      size_ml: it.size_ml || 0,
      retail_tiers: it.retail_tiers || null,
      is_wholesale: it.is_wholesale, accent_color: it.accent_color, selMap: new Map(),
    };
    for (const s of it.selections || []) {
      const boost = s.boost || "standard";
      const k = `${s.flavor_id}|${s.base}|${boost}`;
      const prev = cur.selMap.get(k) || { flavor_id: s.flavor_id, flavor_name: s.flavor_name, base: s.base, boost, quantity: 0 };
      prev.quantity += (s.quantity || 0) * q;
      cur.selMap.set(k, prev);
    }
    bySize.set(it.size_id, cur);
  }

  const mixDiscountBase = bySize.size >= 2 ? mixDiscountRate() : 0;
  const totalMl = [...bySize.values()].reduce(
    (a, cur) => a + (cur.size_ml || 0) * [...cur.selMap.values()].reduce((s, x) => s + x.quantity, 0),
    0
  );
  const mixDiscount = Math.max(mixDiscountBase, tradeVolumeRate(totalMl));
  const merged = [];
  for (const cur of bySize.values()) {
    const bottle_count = [...cur.selMap.values()].reduce((a, s) => a + s.quantity, 0);
    const base_unit = r2(tierUnitPrice(cur.tiers, bottle_count) * (1 - mixDiscount));
    const selections = [...cur.selMap.values()].map((s) => {
      const unit = r2(base_unit * boostMult(s.boost));
      return { ...s, boost_label: boostLabel(s.boost), unit_price: unit, line_total: r2(unit * s.quantity) };
    });
    const box_price = r2(selections.reduce((a, s) => a + s.line_total, 0));
    merged.push({
      key: cur.key, type: "mixbox", size_id: cur.size_id, size_label: cur.size_label,
      tiers: cur.tiers, size_weight_g: cur.size_weight_g, size_ml: cur.size_ml, retail_tiers: cur.retail_tiers, is_wholesale: cur.is_wholesale, accent_color: cur.accent_color,
      bottle_count, selections, quantity: 1,
      base_unit_price: base_unit, unit_price: base_unit, box_price, mixpack_discount: mixDiscount,
      weight_g: (cur.size_weight_g || 0) * bottle_count,
    });
  }
  return [...passthrough, ...merged];
}

// Current box price for a mixbox cart item (falls back to unit_price × bottle_count for legacy items).
export function mixboxBoxPrice(item) {
  if (item.box_price != null) return item.box_price;
  return r2((item.unit_price || 0) * (item.bottle_count || 1));
}

// Standard-retail price of a mixbox (trade accounts only) — used to show "vs retail" savings.
// Returns null if no retail tiers are stored on the item.
export function mixboxRetailPrice(item) {
  if (item.type !== "mixbox" || !Array.isArray(item.retail_tiers) || !item.retail_tiers.length) return null;
  const retailUnit = tierUnitPrice(item.retail_tiers, item.bottle_count || 1);
  const total = (item.selections || []).reduce((a, s) => a + r2(retailUnit * boostMult(s.boost)) * (s.quantity || 0), 0);
  return r2(total);
}

// Compute the price of converting an entire mixbox to a single strength.
// Returns { boost, box_price, delta, selections } or null if the box is already fully that strength.
export function boxUpgradeTo(item, boost) {
  if (item.type !== "mixbox" || !item.selections) return null;
  const baseUnit = item.base_unit_price ?? item.unit_price;
  if (!baseUnit) return null;
  const alreadyAll = item.selections.every((s) => (s.boost || "standard") === boost);
  if (alreadyAll) return null;
  const unit = r2(baseUnit * boostMult(boost));
  const selections = item.selections.map((s) => ({
    ...s, boost, boost_label: boostLabel(boost),
    unit_price: unit, line_total: r2(unit * s.quantity),
  }));
  const box_price = r2(selections.reduce((acc, s) => acc + s.line_total, 0));
  const delta = r2((box_price - mixboxBoxPrice(item)) * (item.quantity || 1));
  const bottles = item.bottle_count || item.selections.reduce((a, s) => a + (s.quantity || 0), 0) || 1;
  const per_bottle = r2((box_price - mixboxBoxPrice(item)) / bottles);
  return { boost, box_price, delta, per_bottle, selections, short: boost === "x2" ? "×2" : "×3" };
}

// Strength ladder: a fully Standard box is offered ×2, a fully ×2 box is offered ×3, a ×3 box nothing.
export function nextBoxUpgrade(item) {
  if (item.type !== "mixbox" || !item.selections?.length) return null;
  const boosts = new Set(item.selections.map((s) => s.boost || "standard"));
  if (boosts.size !== 1) return null;
  const cur = [...boosts][0];
  const next = cur === "standard" ? "x2" : cur === "x2" ? "x3" : null;
  if (!next) return null;
  const up = boxUpgradeTo(item, next);
  return up && up.delta > 0 ? up : null;
}
